import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, isValid } from "date-fns";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { supabaseBikeService } from "@/api/supabaseBikeService";
import { Clock, User, Ruler, Leaf } from "lucide-react";
import { toast } from "sonner";
import { calculateUserCO2Offset } from "@/api/userService";

interface BikeRideHistoryProps {
  bikeId: string;
}

const BikeRideHistory: React.FC<BikeRideHistoryProps> = ({ bikeId }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  
  // Use the supabaseBikeService directly instead of bikeService
  const { data, isLoading, error } = useQuery({
    queryKey: ["bikeRides", bikeId, currentPage, pageSize],
    queryFn: () => supabaseBikeService.getBikeRides(bikeId, currentPage, pageSize),
  });
  
  if (error) {
    console.error(`Error fetching rides for bike ${bikeId}:`, error);
    return (
      <div className="flex flex-col items-center justify-center rounded-md border py-8 text-center">
        <div className="rounded-full bg-red-100 p-3">
          <span className="text-red-600">!</span>
        </div>
        <h3 className="mt-3 text-sm font-medium">Error Loading Ride History</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          We encountered an error loading this bike's ride history.
        </p>
        <Button variant="link" className="mt-2" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>
    );
  }
  
  if (isLoading) {
    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Date & Time</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Distance</TableHead>
              <TableHead>CO2 Offset</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, index) => (
              <TableRow key={index}>
                <TableCell><Skeleton className="h-4 w-[120px]" /></TableCell>
                <TableCell><Skeleton className="h-4 w-[150px]" /></TableCell>
                <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }
  
  const rides = data?.rides || [];
  const totalItems = data?.total || 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  
  if (rides.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-md border py-8 text-center">
        <div className="rounded-full bg-muted p-3">
          <Clock className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="mt-3 text-sm font-medium">No Ride History</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          This bike doesn't have any recorded rides yet.
        </p>
      </div>
    );
  }

  const formatSafeDate = (dateString: Date | string) => {
    const date = new Date(dateString);
    return isValid(date) ? date : new Date();
  };
  
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-1/5">User</TableHead>
            <TableHead className="w-1/5">Date & Time</TableHead>
            <TableHead className="w-1/5">Duration</TableHead>
            <TableHead className="w-1/5">Distance</TableHead>
            <TableHead className="w-1/5">CO2 Offset</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rides.map((ride) => (
            <TableRow key={ride.id} className="hover:bg-muted/50">
              <TableCell>
                <Link 
                  to={`/users/${ride.userId}`}
                  className="flex items-center gap-2 hover:underline"
                >
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{ride.userName}</span>
                </Link>
              </TableCell>
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-medium">
                    {format(formatSafeDate(ride.startTime), "dd MMM yyyy")}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {format(formatSafeDate(ride.startTime), "HH:mm")}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{ride.duration} minutes</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Ruler className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{ride.distance.toFixed(2)} km</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Leaf className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">
                    {ride.countTowardsOffset ? `${calculateUserCO2Offset(ride.distance).toFixed(2)} kg` : "Not counted"}
                  </span>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t px-4 py-2">
          <div className="text-sm text-muted-foreground">
            Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{" "}
            <span className="font-medium">
              {Math.min(currentPage * pageSize, totalItems)}
            </span>{" "}
            of <span className="font-medium">{totalItems}</span> rides
          </div>
          <div className="space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BikeRideHistory;
